import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Send, Sparkles, Plus, MessageSquare, Trash2, PanelLeft } from "lucide-react";
import { api, streamChat } from "@/lib/api";
import { track } from "@/lib/analytics";

const SUGGESTIONS = [
  "Prepare today's priorities",
  "Summarise yesterday",
  "Plan this week's work",
  "Draft a customer follow-up",
  "Review business performance",
  "Show pending commitments",
];

export default function AIChat({ variant = "default" }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const scrollRef = useRef(null);
  const streamIdRef = useRef(0);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await api.get("/ai/conversations");
      setConversations(data.conversations || []);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const openConversation = async (id) => {
    streamIdRef.current += 1;
    setBusy(false);
    setShowHistory(false);
    try {
      const { data } = await api.get(`/ai/conversations/${id}/messages`);
      setMessages((data.messages || []).map((m) => ({ role: m.role, content: m.content, actions: m.actions || [] })));
      setConversationId(id);
    } catch {
      /* noop */
    }
  };

  const deleteConversation = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai/conversations/${id}`);
      setConversations((c) => c.filter((x) => x.conversation_id !== id));
      if (id === conversationId) newChat();
    } catch {
      /* noop */
    }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || busy) return;
    setInput("");
    setBusy(true);
    track("ai_chat_message");
    const myId = ++streamIdRef.current;
    const isStale = () => streamIdRef.current !== myId;
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "", actions: [] }]);

    await streamChat(content, conversationId, {
      onDelta: (delta) => {
        if (isStale()) return;
        setMessages((m) => {
          if (m.length === 0 || m[m.length - 1].role !== "assistant") return m;
          const copy = [...m];
          copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + delta };
          return copy;
        });
      },
      onActions: (list) => {
        if (isStale()) return;
        setMessages((m) => {
          if (m.length === 0 || m[m.length - 1].role !== "assistant") return m;
          const copy = [...m];
          const last = copy[copy.length - 1];
          // Strip <<<ACTIONS ... ACTIONS>>> from the visible content
          const cleaned = (last.content || "").replace(/<<<ACTIONS[\s\S]*?ACTIONS>>>/g, "").trimEnd();
          copy[copy.length - 1] = { ...last, content: cleaned, actions: list };
          return copy;
        });
      },
      onDone: (payload) => {
        if (isStale()) return;
        if (payload.conversation_id) setConversationId(payload.conversation_id);
        // Final safety strip in case no actions were emitted mid-stream
        setMessages((m) => {
          if (m.length === 0 || m[m.length - 1].role !== "assistant") return m;
          const copy = [...m];
          const last = copy[copy.length - 1];
          const cleaned = (last.content || "").replace(/<<<ACTIONS[\s\S]*?ACTIONS>>>/g, "").trimEnd();
          copy[copy.length - 1] = { ...last, content: cleaned };
          return copy;
        });
        setBusy(false);
        loadConversations();
      },
      onError: () => {
        if (isStale()) return;
        setMessages((m) => {
          if (m.length === 0 || m[m.length - 1].role !== "assistant") return m;
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: "I couldn't reach the AI service. Please try again." };
          return copy;
        });
        setBusy(false);
      },
    });
  };

  const newChat = () => {
    streamIdRef.current += 1;
    setMessages([]);
    setConversationId(null);
    setInput("");
    setBusy(false);
    setShowHistory(false);
  };

  const empty = messages.length === 0;

  return (
    <section
      className={
        variant === "compact"
          ? "flex h-full min-h-[520px] overflow-hidden rounded-2xl border border-border bg-card"
          : "flex h-[560px] overflow-hidden rounded-3xl border border-border bg-card"
      }
      data-testid="ai-chat"
    >
      {/* History panel — hidden entirely in compact variant */}
      {variant !== "compact" && (
      <aside
        className={`${showHistory ? "absolute inset-0 z-20 flex bg-card lg:relative lg:inset-auto" : "hidden lg:flex"} w-full flex-col border-r border-border lg:w-60`}
        data-testid="ai-history-panel"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">History</span>
          <button onClick={newChat} data-testid="ai-new-chat-btn" className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground">
            <Plus className="h-3.5 w-3.5" /> New
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {conversations.length === 0 ? (
            <p className="px-2 py-4 text-xs text-muted-foreground">No conversations yet.</p>
          ) : (
            conversations.map((c) => (
              <button
                key={c.conversation_id}
                onClick={() => openConversation(c.conversation_id)}
                data-testid={`ai-conversation-${c.conversation_id}`}
                className={`group flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${
                  c.conversation_id === conversationId ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/60"
                }`}
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0" />
                <span className="flex-1 truncate">{c.title || "Untitled"}</span>
                <span onClick={(e) => deleteConversation(c.conversation_id, e)} className="opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100" title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </span>
              </button>
            ))
          )}
        </div>
      </aside>
      )}

      {/* Chat */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between gap-2 border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2">
            {variant !== "compact" && (
              <button onClick={() => setShowHistory(true)} className="inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border text-muted-foreground lg:hidden" data-testid="ai-history-toggle" aria-label="History">
                <PanelLeft className="h-4 w-4" />
              </button>
            )}
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-lg brand-gradient-bg text-white">
              <Sparkles className="h-3.5 w-3.5" />
            </span>
            <h2 className="font-display text-sm font-semibold tracking-tight">Ask your business</h2>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="ai-chat-messages">
          {empty ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <div className="max-w-md">
                <h3 className="font-display text-lg font-semibold tracking-tight">What should we work on?</h3>
                <p className="mt-2 text-sm text-muted-foreground">Ask naturally — plan your day, draft communications, review performance or search your business.</p>
                <div className="mt-5 flex flex-wrap justify-center gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid={`ai-suggestion-${s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`} className="rounded-full border border-border bg-background px-3.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:border-brand-teal/40 hover:text-foreground">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] space-y-2 rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-foreground text-background" : "border border-border bg-background/60 text-foreground"}`}>
                  <div className="whitespace-pre-wrap">
                    {m.content || <span className="inline-flex gap-1"><Dot /><Dot d={0.15} /><Dot d={0.3} /></span>}
                  </div>
                  {m.actions && m.actions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 pt-1" data-testid="ai-actions">
                      {m.actions.map((a, idx) => (
                        <span key={idx} data-testid={`ai-action-${a.kind}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${a.ok ? "bg-brand-teal/10 text-brand-teal ring-brand-teal/30" : "bg-red-500/10 text-brand-red ring-red-500/30"}`}>
                          {a.ok ? "✓" : "!"} {a.summary}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t border-border p-3" data-testid="ai-chat-form">
          <div className="flex items-end gap-2 rounded-2xl border border-border bg-background px-3 py-2 focus-within:border-primary">
            <textarea rows={1} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder="Message your business…" data-testid="ai-chat-input" className="max-h-32 flex-1 resize-none bg-transparent py-1 text-sm outline-none" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="ai-chat-send-btn" className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl brand-gradient-bg text-white transition-opacity disabled:opacity-40">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function Dot({ d = 0 }) {
  return <motion.span className="h-1.5 w-1.5 rounded-full bg-muted-foreground" animate={{ opacity: [0.3, 1, 0.3] }} transition={{ duration: 1, repeat: Infinity, delay: d }} />;
}
